import React, { useState, useEffect, useCallback } from "react";
import MedicationsTemplate from "components/templates/MedicationsTemplate";
import Text from "components/atoms/Text";
import {
  fetchDrugs,
  fetchDrugLabelById,
  fetchDrugLabelByItemCode,
  OffsetParams,
  ApiResponse,
} from "api/drugService";
import Spinner from "components/atoms/Spinner";
import { UserRole, Drug, DrugProductLabel } from "common/models/types";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { log, LogLevel } from "utils/LogUtil";

const LIMIT = 20; // 한 번에 로드할 항목 수

const Medications: React.FC = () => {
  // 의약품 데이터 상태
  const [drugs, setDrugs] = useState<Drug[]>([]);
  // 선택된 의약품 상태
  const [selectedDrug, setSelectedDrug] = useState<Drug | null>(null);
  // 현재 검색어
  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>("");
  // 로딩 상태
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 에러 상태
  const [error, setError] = useState<string | null>(null);
  // 더 불러올 수 있는지 여부
  const [hasMore, setHasMore] = useState<boolean>(true);
  // 더 로드 중인지 여부
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  // 라벨 로딩 중 여부
  const [isLoadingLabels, setIsLoadingLabels] = useState<boolean>(false);

  // 현재 사용자 정보
  const { user } = useSelector((state: RootState) => state.user);

  // 의약품 목록 가져오기
  const loadDrugs = useCallback(
    async (isInitialLoad = false, searchQuery: string = currentSearchQuery) => {
      try {
        if (isInitialLoad) {
          setIsLoading(true);
          // 초기 로드시 선택된 의약품 초기화
          setSelectedDrug(null);
        } else {
          setIsLoadingMore(true);
        }

        // offset 계산 - 첫 로드면 0, 아니면 현재 drugs 길이 사용
        const offset = isInitialLoad ? 0 : drugs.length;

        // 실제 API 호출
        const response = await fetchDrugs({
          offset,
          limit: LIMIT,
          crawledFirst: true,
          search: searchQuery || undefined,
        });

        // 응답 유효성 확인
        if (response.error === 0 && response.data) {
          if (isInitialLoad) {
            setDrugs(response.data);
          } else {
            setDrugs((prev) => [...prev, ...response.data]);
          }

          // 메타 데이터가 있으면 사용, 없으면 false로 설정
          setHasMore(response.meta?.hasMore ?? false);
        } else {
          // 에러 응답이거나 데이터가 없는 경우
          log(LogLevel.ERROR, "API 응답 오류:", response.message || "알 수 없는 오류");
          setError(response.message || "의약품 목록을 불러오는 데 실패했습니다.");
        }

        setIsLoading(false);
        setIsLoadingMore(false);
      } catch (err) {
        log(LogLevel.ERROR, "의약품 목록을 불러오는 데 실패했습니다:", err);
        setError("의약품 목록을 불러오는 데 실패했습니다.");
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [currentSearchQuery, drugs.length]
  );

  // 초기 로드
  useEffect(() => {
    loadDrugs(true);
  }, []);

  // 검색 핸들러
  const handleSearch = useCallback(
    (query: string) => {
      setCurrentSearchQuery(query);
      loadDrugs(true, query);
    },
    [loadDrugs]
  );

  // 추가 데이터 로드 핸들러
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      loadDrugs(false);
    }
  }, [hasMore, isLoadingMore, loadDrugs]);

  // 의약품 라벨 정보 로드
  const loadDrugLabels = async (drug: Drug): Promise<DrugProductLabel[]> => {
    if (!drug.itemCode) {
      log(LogLevel.ERROR, "약품 품목코드가 없습니다:", drug);
      return [];
    }

    setIsLoadingLabels(true);
    try {
      // 품목코드로 조회
      log(LogLevel.VERBOSE, "품목코드로 조회:", drug.itemCode);
      const labels = await fetchDrugLabelByItemCode(drug.itemCode);

      log(LogLevel.VERBOSE, "로드된 라벨:", labels);
      setIsLoadingLabels(false);
      return labels;
    } catch (err) {
      log(LogLevel.ERROR, "의약품 허가사항을 불러오는 데 실패했습니다:", err);
      setIsLoadingLabels(false);
      return [];
    }
  };

  // 의약품 선택 핸들러
  const handleSelectDrug = async (drug: Drug) => {
    log(LogLevel.INFO, "선택된 약품:", drug);

    // 필수 속성 확인
    if (!drug.standardCode) {
      log(LogLevel.ERROR, "선택된 약품에 표준코드가 없습니다:", drug);
      return;
    }

    // 이미 선택된 약품을 다시 클릭하면 선택 해제
    if (selectedDrug && selectedDrug.standardCode === drug.standardCode) {
      setSelectedDrug(null);
      return;
    }

    // 약품에 productLabels가 없거나 비어있으면 새로 로드
    if (!drug.productLabels || drug.productLabels.length === 0) {
      log(LogLevel.INFO, "약품에 허가사항이 없어 로드합니다.");
      const labels = await loadDrugLabels(drug);

      // 기존 drugs 배열에서 현재 약품 업데이트
      setDrugs((prevDrugs) =>
        prevDrugs.map((item) =>
          item.standardCode === drug.standardCode ? { ...item, productLabels: labels } : item
        )
      );

      // 선택된 약품에 라벨 정보 추가
      setSelectedDrug({
        ...drug,
        productLabels: labels,
      });
    } else {
      log(LogLevel.INFO, "약품에 이미 허가사항이 있습니다:", drug.productLabels);
      setSelectedDrug(drug);
    }
  };

  // 의약품 수정 핸들러 (추후 구현)
  const handleEditDrug = () => {
    // 수정 기능 구현
    log(LogLevel.INFO, `의약품 수정: ${selectedDrug?.standardCode}`);
  };

  // 라벨 로드 완료 핸들러 (크롤링 후 호출)
  const handleLabelsLoaded = useCallback(
    (labels: DrugProductLabel[], itemCode: string) => {
      log(LogLevel.INFO, "크롤링 후 라벨 로드 완료:", labels);

      // 선택된 약품의 라벨 업데이트
      if (selectedDrug && selectedDrug.itemCode === itemCode) {
        setSelectedDrug({
          ...selectedDrug,
          productLabels: labels,
        });
      }

      // 전체 약품 목록에서도 해당 약품의 라벨 업데이트
      setDrugs((prevDrugs) =>
        prevDrugs.map((item) =>
          item.itemCode === itemCode ? { ...item, productLabels: labels } : item
        )
      );
    },
    [selectedDrug]
  );

  // 로딩 중인 경우 (첫 로드)
  if (isLoading && drugs.length === 0) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}
      >
        <Spinner />
      </div>
    );
  }

  // 에러가 발생한 경우
  if (error) {
    return (
      <div style={{ padding: "24px" }}>
        <Text textType="H3">오류가 발생했습니다</Text>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <MedicationsTemplate
      drugs={drugs}
      selectedDrug={selectedDrug}
      onSelectDrug={handleSelectDrug}
      onEditDrug={handleEditDrug}
      onSearch={handleSearch}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      isLoadingLabels={isLoadingLabels}
      onLoadMore={handleLoadMore}
      onLabelsLoaded={handleLabelsLoaded}
    />
  );
};

export default Medications;
